import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { deleteItem, getItem, setItem } from '../../../utils/storage'
import {
  DAILY_TIP_DISMISSED_DATE_STORAGE_KEY,
  getLocalDateStamp,
  isDailyTipDismissedToday,
} from '../utils/daily-tip-visibility'

const dashboardTipVisibilityQueryKey = () =>
  [DAILY_TIP_DISMISSED_DATE_STORAGE_KEY, getLocalDateStamp()] as const

export function useDashboardTipVisibility() {
  const queryClient = useQueryClient()
  const queryKey = dashboardTipVisibilityQueryKey()
  // Avoid showing the tip until AsyncStorage tells us whether it was dismissed today.
  const { data: hideTip } = useQuery({
    queryKey,
    queryFn: () =>
      getItem(DAILY_TIP_DISMISSED_DATE_STORAGE_KEY).then(
        isDailyTipDismissedToday,
      ),
    staleTime: Infinity,
    placeholderData: true,
  })
  const handleDismissTip = useCallback(() => {
    queryClient.setQueryData(queryKey, true)
    setItem(DAILY_TIP_DISMISSED_DATE_STORAGE_KEY, getLocalDateStamp()).catch(
      (error) => console.warn('Failed to persist daily tip dismissal', error),
    )
  }, [queryClient, queryKey])

  const handleShowTip = useCallback(() => {
    queryClient.setQueryData(queryKey, false)
    deleteItem(DAILY_TIP_DISMISSED_DATE_STORAGE_KEY).catch((error) =>
      console.warn('Failed to clear daily tip dismissal', error),
    )
  }, [queryClient, queryKey])

  return {
    showTip: hideTip === false,
    handleDismissTip,
    handleShowTip,
  }
}
